import { DateTime } from "luxon";

import { IBFAlarm, IBF_ALARM } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_ALARM";
import { IBFBasalRate, IBF_BASAL_RATE } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_BASAL_RATE";
import { IBFDeactivate, IBF_DEACTIVATE } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_DEACTIVATE";
import { IBFSuspend, IBF_SUSPEND } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_SUSPEND";
import { IBFLogRecord, IBFLogRecordHistory } from "@/lib/drivers/insulet/IBFRecords/IBF_LOG_RECORD";
import { DateUtils } from "@/utils/dateUtils";

export type BasalDeliveryRange = {
  logIndex: number;
  basalRate: number;
  tempRate: boolean;
  tempRatePercent: number;
  start: DateTime;
  end: DateTime;
};

export type DailyBasalDeliveryRange = {
  day: DateTime;
  ranges: BasalDeliveryRange[];
};

export type IBGInterruptionEvent =
  | IBFLogRecordHistory<IBFDeactivate>
  | IBFLogRecordHistory<IBFSuspend>
  | IBFLogRecordHistory<IBFAlarm>;

export const isBasalRateEvent = (event: IBFLogRecord): event is IBFLogRecordHistory<IBFBasalRate> =>
  event.type === "HISTORY" && event.history.type === IBF_BASAL_RATE.value;

const isDeactivate = (event: IBFLogRecord): event is IBFLogRecordHistory<IBFDeactivate> =>
  event.type === "HISTORY" && event.history.type === IBF_DEACTIVATE.value;

const isSuspend = (event: IBFLogRecord): event is IBFLogRecordHistory<IBFSuspend> =>
  event.type === "HISTORY" && event.history.type === IBF_SUSPEND.value;

const isAlarm = (event: IBFLogRecord): event is IBFLogRecordHistory<IBFAlarm> =>
  event.type === "HISTORY" && event.history.type === IBF_ALARM.value;

export const isBasalInterruptionEvent = (event: IBFLogRecord): event is IBGInterruptionEvent =>
  isDeactivate(event) || isSuspend(event) || (isAlarm(event) && event.history.data.stopsDelivery);

type FormattedBasalRate = {
  basalRate: number;
  tempRate: boolean;
  tempRatePercent: number;
};

export const getBasalRate = (event: IBFBasalRate): FormattedBasalRate => {
  const tempRatePercentage = event.percentage;

  return {
    basalRate: event.basalRate,
    tempRate: tempRatePercentage !== 1,
    tempRatePercent: tempRatePercentage,
  };
};

/**
 * Checks if the provided date range is valid.
 *
 * @param {DateTime | undefined} start - The start date and time of the range. This value may be undefined.
 * @param {DateTime | undefined} end - The end date and time of the range. This value may be undefined.
 * @returns {boolean} Returns true if both start and end are defined and the difference between them in minutes is greater than 1; otherwise, returns false.
 */
const isValidRange = (start: DateTime | undefined, end: DateTime | undefined): boolean =>
  !!start && !!end && end.diff(start, "seconds").seconds > 1;

/**
 * Calculates the basal insulin delivery ranges based on a series of basal rate events,
 * basal delivery interruption events, and new day events. These events are combined and
 * sequentially processed to generate a list of time intervals with corresponding basal delivery data.
 *
 * @param {IBFLogRecordHistory<IBFBasalRate>[]} basalRateEvents - An array of basal rate log record histories.
 * These events represent changes in basal insulin delivery rates over time.
 * @param {IBGInterruptionEvent[]} basalDeliveryInterruptEvent - An array of events representing interruptions in basal insulin delivery.
 * These interruptions may be temporary and include the exact timestamps of when they occurred.
 * @param {IBFLogRecordHistory<IBFNewDay>[]} newDayEvents - An array of log record histories representing the start of a new day.
 * This is used to handle cross-day boundaries in basal insulin delivery calculations.
 * @returns {BasalDeliveryRange[]} An array of calculated basal delivery ranges. Each object includes the start and end time of the range,
 * basal rate information, and relevant log index data.
 */
export const getBasalInsulinDeliveryRanges = (
  basalRateEvents: IBFLogRecordHistory<IBFBasalRate>[],
  basalDeliveryInterruptEvent: IBGInterruptionEvent[],
): BasalDeliveryRange[] => {
  const events = [...basalDeliveryInterruptEvent, ...basalRateEvents].toSorted((a, b) =>
    DateUtils.compareDateTimes(a.date, b.date),
  );
  let index = 0;
  const ranges: BasalDeliveryRange[] = [];

  while (index + 1 < events.length) {
    const event = events[index];
    const nextEvent = events[index + 1];
    if (isBasalRateEvent(event)) {
      const rate = getBasalRate(event.history.data);
      ranges.push({
        logIndex: event.logIndex,
        ...rate,
        start: event.date,
        end: nextEvent.date,
      });
    } else if (isBasalInterruptionEvent(event)) {
      ranges.push({
        logIndex: event.logIndex,
        basalRate: 0,
        tempRate: false,
        tempRatePercent: 1,
        start: event.date,
        end: nextEvent.date,
      });
    }
    index += 1;
  }

  return ranges;
};

/**
 * Splits an array of basal delivery ranges into multiple daily ranges based on their start and end times.
 *
 * If a range spans across midnight, it is split into two ranges: one for the first day and another for the second day.
 * The resulting list of ranges ensures that each entry corresponds to a specific day, with no range crossing over to another day.
 *
 * ** VIBE CODED **
 *
 * @param {BasalDeliveryRange[]} ranges - An array of basal delivery ranges, where each range includes start and end DateTime values.
 * @returns {DailyBasalDeliveryRange[]} An array of daily basal delivery ranges, each containing a specific day and the ranges associated with that day.
 */
export const splitRangesByDay = (ranges: BasalDeliveryRange[]): DailyBasalDeliveryRange[] => {
  const result: DailyBasalDeliveryRange[] = [];
  let currentDay: DateTime | undefined = undefined;
  let currentRanges: BasalDeliveryRange[] = [];

  for (const range of ranges) {
    const startDay = range.start.startOf("day").set({ hour: 12 });
    const endDay = range.end.startOf("day").set({ hour: 12 });

    if (!startDay.equals(endDay)) {
      // Split range into two parts at midnight
      const midnightTime = range.end.startOf("day");

      if (isValidRange(range.start, midnightTime)) {
        const firstRange = {
          ...range,
          end: midnightTime,
        };

        if (!currentDay || !currentDay.equals(startDay)) {
          if (currentDay && currentRanges.length > 0) {
            result.push({
              day: currentDay,
              ranges: currentRanges,
            });
          }
          currentDay = startDay;
          currentRanges = [];
        }
        currentRanges.push(firstRange);
      }

      if (isValidRange(midnightTime, range.end)) {
        const secondRange = {
          ...range,
          start: midnightTime,
        };

        if (currentDay && currentRanges.length > 0) {
          result.push({
            day: currentDay,
            ranges: currentRanges,
          });
        }
        currentDay = endDay;
        currentRanges = [secondRange];
      }
    } else {
      if (!currentDay || !currentDay.equals(startDay)) {
        if (currentDay && currentRanges.length > 0) {
          result.push({
            day: currentDay,
            ranges: currentRanges,
          });
        }
        currentDay = startDay;
        currentRanges = [];
      }

      if (isValidRange(range.start, range.end)) {
        currentRanges.push(range);
      }
    }
  }

  if (currentDay && currentRanges.length > 0) {
    result.push({
      day: currentDay,
      ranges: currentRanges,
    });
  }

  return result;
};
